/**
 * Contractor SOP Library
 * ======================
 * Standards and procedures for contractor engagement.
 * 
 * Route: /wyldecrest/helpdesk/contractor-standards
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  EscalationPath,
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  Users, 
  Shield, 
  FileCheck, 
  Clock, 
  AlertTriangle,
  Award,
  BadgeCheck
} from 'lucide-react';

const sections = [
  { id: 'contractor-requirements', title: 'Contractor Requirements' },
  { id: 'onboarding-process', title: 'Onboarding Process' },
  { id: 'performance-standards', title: 'Performance Standards' },
  { id: 'compliance-requirements', title: 'Compliance Requirements' },
  { id: 'escalation-process', title: 'Issue & Escalation Process' },
];

export default function ContractorStandardsPage() {
  return (
    <GovernancePageLayout
      title="Contractor SOP Library"
      subtitle="Standards and procedures for contractor engagement and management"
      icon={Users}
      version="1.5"
      lastReviewed="2026-05-18"
      reviewedBy="Operations Director"
      sections={sections}
    >
      {/* Contractor Requirements */}
      <DocumentSection title="Contractor Requirements" icon={Shield} id="contractor-requirements">
        <p className="text-slate-300 mb-6">
          All contractors must meet minimum requirements before being approved to work on PHS-managed properties.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Mandatory Requirements</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-semibold mb-2">Insurance</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Public Liability: £5M minimum</li>
              <li>• Employers Liability: £10M minimum</li>
              <li>• Professional Indemnity: £1M (where applicable)</li>
              <li>• Evidence must be renewed annually</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Certifications</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Trade-specific qualifications</li>
              <li>• Gas Safe (Gas work only)</li>
              <li>• NICEIC/NAPIT (Electrical only)</li>
              <li>• OFTEC (Oil heating only)</li>
            </ul>
          </div>
          
          <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
            <h4 className="text-purple-300 font-semibold mb-2">Documentation</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Company registration details</li>
              <li>• VAT registration (if applicable)</li>
              <li>• Bank details for payments</li>
              <li>• Key contact information</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Operational</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Health & Safety policy</li>
              <li>• Risk assessment capability</li>
              <li>• PPE compliance</li>
              <li>• Vehicle roadworthiness</li>
            </ul>
          </div>
        </div>

        <WarningBox title="Compliance Lock">
          Contractors with expired insurance or certifications are automatically locked
          from receiving new job assignments until documentation is renewed.
        </WarningBox>
      </DocumentSection>

      {/* Onboarding Process */}
      <DocumentSection title="Onboarding Process" icon={BadgeCheck} id="onboarding-process">
        <p className="text-slate-300 mb-6">
          New contractors must complete the onboarding process before being activated.
        </p>

        <SOPStep 
          number={1}
          title="Application Review"
          description="Review contractor application and supporting documentation."
          substeps={[
            'Verify company registration',
            'Check insurance certificates',
            'Validate trade certifications',
            'Confirm coverage area'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Reference Check"
          description="Conduct reference and quality checks."
          substeps={[
            'Request 2 trade references',
            'Contact references for feedback',
            'Review any existing online reviews',
            'Document findings in contractor record'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="System Setup"
          description="Create contractor account and provide access."
          substeps={[
            'Create contractor profile in system',
            'Configure trade types and coverage',
            'Set up portal access credentials',
            'Send welcome pack and SOP guide'
          ]}
        />

        <SOPStep 
          number={4}
          title="Trial Period"
          description="Monitor performance during initial 30-day trial."
          substeps={[
            'Assign 3-5 trial jobs',
            'Closely monitor response times',
            'Review completion quality',
            'Collect any feedback from site'
          ]}
        />

        <Checklist 
          title="Onboarding Completion Checklist"
          items={[
            'All insurance documents verified and uploaded',
            'Trade certifications validated',
            'Portal access tested and confirmed',
            'Bank details verified for payments',
            'SOP acknowledgement received',
            'Trial period initiated',
            'Compliance review date set'
          ]}
        />
      </DocumentSection>

      {/* Performance Standards */}
      <DocumentSection title="Performance Standards" icon={Award} id="performance-standards">
        <p className="text-slate-300 mb-6">
          Contractors are evaluated on key performance indicators with minimum thresholds.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">KPI Targets</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Metric</th>
                <th className="pb-2">Minimum</th>
                <th className="pb-2">Target</th>
                <th className="pb-2">Excellent</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Response Rate</td>
                <td className="text-red-400">70%</td>
                <td className="text-amber-400">85%</td>
                <td className="text-emerald-400">95%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">SLA Compliance</td>
                <td className="text-red-400">80%</td>
                <td className="text-amber-400">90%</td>
                <td className="text-emerald-400">98%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">First-Time Fix</td>
                <td className="text-red-400">65%</td>
                <td className="text-amber-400">80%</td>
                <td className="text-emerald-400">90%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Quality Score</td>
                <td className="text-red-400">3.5/5</td>
                <td className="text-amber-400">4.0/5</td>
                <td className="text-emerald-400">4.5/5</td>
              </tr>
              <tr>
                <td className="py-2">Invoice Accuracy</td>
                <td className="text-red-400">90%</td>
                <td className="text-amber-400">95%</td>
                <td className="text-emerald-400">99%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Performance Actions</h3>
        
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-emerald-900/20 border border-emerald-700/50 rounded-lg">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-emerald-300 font-medium">Green (>90%)</span>
            <span className="text-slate-400 text-sm">→ Priority assignment, rate review eligible</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-amber-300 font-medium">Amber (70-90%)</span>
            <span className="text-slate-400 text-sm">→ Standard assignment, performance review</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-red-300 font-medium">Red (&lt;70%)</span>
            <span className="text-slate-400 text-sm">→ Restricted assignment, improvement plan required</span>
          </div>
        </div>
      </DocumentSection>

      {/* Compliance Requirements */}
      <DocumentSection title="Compliance Requirements" icon={FileCheck} id="compliance-requirements">
        <p className="text-slate-300 mb-6">
          Ongoing compliance requirements contractors must maintain.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Document Renewal Schedule</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Document</th>
                <th className="pb-2">Renewal</th>
                <th className="pb-2">Warning</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Public Liability Insurance</td>
                <td>Annual</td>
                <td>60 days before expiry</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Employers Liability</td>
                <td>Annual</td>
                <td>60 days before expiry</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Gas Safe Registration</td>
                <td>Annual</td>
                <td>90 days before expiry</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Electrical Certifications</td>
                <td>As per body</td>
                <td>90 days before expiry</td>
              </tr>
              <tr>
                <td className="py-2">Vehicle Insurance</td>
                <td>Annual</td>
                <td>30 days before expiry</td>
              </tr>
            </tbody>
          </table>
        </div>

        <InfoBox title="Automatic Notifications">
          The system automatically sends renewal reminders at the warning threshold.
          Contractors receive reminders at 60, 30, and 7 days before expiry.
        </InfoBox>
      </DocumentSection>

      {/* Escalation Process */}
      <DocumentSection title="Issue & Escalation Process" icon={AlertTriangle} id="escalation-process">
        <p className="text-slate-300 mb-6">
          Process for handling contractor issues, complaints, and escalations.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Escalation Path</h3>
        <EscalationPath levels={['Helpdesk', 'Team Lead', 'Ops Manager', 'Commercial Director']} />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Issue Categories</h3>
        
        <div className="space-y-4">
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
            <h4 className="text-red-300 font-semibold mb-2">Critical (Immediate Suspension)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Safety incident on site</li>
              <li>• Fraud or misrepresentation</li>
              <li>• Gross misconduct</li>
              <li>• Major compliance breach</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Serious (Warning + Review)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Repeated SLA breaches</li>
              <li>• Quality complaints (3+ in 30 days)</li>
              <li>• Invoice disputes</li>
              <li>• Communication failures</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Minor (Feedback + Monitoring)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Single SLA miss</li>
              <li>• Documentation incomplete</li>
              <li>• Minor quality issue</li>
              <li>• Late response</li>
            </ul>
          </div>
        </div>
      </DocumentSection>
    </GovernancePageLayout>
  );
}
